import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import React, { useState ,useCallback} from "react";
import { formatNumber } from "../../utils/UtilFunctions";
import useFormField from "./useFormField";
import { getUpdatedOptions } from "./formUtils";
import FormCalendarDate from "./FormCalendarDate";

const InputReadOnlyLabel = ({name, value, style}) => {
  const [inputWidth, setInputWidth] = useState();

  return (
    <InputText
      id={`readonly-${name}`}
      value={value}
      readOnly
      key={`${name}-${value}`}
      className="input-readonly-label"
      data-input-not-focus
      tabIndex={-1}
      style={{ minWidth: "100%", width: inputWidth, ...style }}
      ref={(ref) => {
        if (ref) {
          setInputWidth(ref?.scrollWidth);
        }
      }}
    />
  );
};

const FormField = ({
  field = {},
  register,
  control,
  errors,
  Controller,
  dropdownOptions = [],
  mode,
  setValue,
  style,
  getValues,
  fetchLookupData,
  viewLookupDetails,
  dynamicFieldNature = null,
  openedDropdownsRef,
  clearErrors,
  setError,
  getChangedDateValues = null,
  dropdownStyle = null,
}) => {
  const [previousValidValue, setPreviousValidValue] = useState(0);
  const {
    name,
    label,
    type,
    options = {},
    fieldNature,
    fractionDigits,
    numberMode,
    format = "",
    externalProgram = "",
    readOnlyProgram,
    params = [],
    viewMode = "link",
    inputType,
    autoItemWidth = false,
    showDropdownOriginalValue = false,
  } = field;

  const {
    hidden,
    readOnly,
    handleTextChange,
    trimText,
    handleNumberChange,
    handleNumberKeyDown,
    setDate,
    getDate,
    getFieldLabel,
    setDropdownValue,
    getDropdownValue,
    processError,
    navigateToLookup,
  } = useFormField({ setValue, fieldNature, mode, dynamicFieldNature });

  const openLookupModal = useCallback(() => {
    const formData = getValues();
    console.log("field - ", field, " formData - ", formData);
    fetchLookupData(externalProgram, params, formData);
  }, [field, externalProgram, params]);

  const getComponent = () => {
    switch (type) {
      case "text": {
        const updatedOptions = getUpdatedOptions(options, readOnly);
        return (
          <Controller
            name={name}
            control={control}
            rules={updatedOptions}
            render={({ field: { name, value } }) => {
              if (readOnly) {
                return (
                  <InputReadOnlyLabel style={style} name={name} value={value} />
                );
              }
              return (
                <>
                  <InputText
                    id={name}
                    name={name}
                    value={value}
                    className={classNames({
                      "p-invalid": errors[name],
                    })}
                    aria-invalid={errors[name]}
                    onChange={(e) =>
                      handleTextChange(e, options.maxLength?.value)
                    }
                    onBlur={(e) => {
                      trimText(e);
                    }}
                    autoComplete="off"
                    aria-autocomplete="none"
                  />
                  {options["required"] && <sup className="star-label">*</sup>}
                </>
              );
            }}
          />
        );
      }
      case "number":
        return (
          <Controller
            name={name}
            control={control}
            rules={options}
            render={({ field: { name, value } }) => {
              if (readOnly) {
                const formattedValue = formatNumber(value, fractionDigits);
                return (
                  <InputReadOnlyLabel name={name} value={formattedValue} />
                );
              } else {
                return (
                  <>
                    <InputNumber
                      id={name}
                      inputId={name}
                      name={name}
                      value={value}
                      min={options.min?.value}
                      max={options.max?.value}
                      mode={numberMode}
                      minFractionDigits={fractionDigits}
                      maxFractionDigits={fractionDigits}
                      onChange={(e) => {
                        const newValue = e.value;
                        handleNumberChange(
                          name,
                          newValue,
                          previousValidValue,
                          options.min?.value,
                          options.max?.value,
                          fractionDigits
                        );
                        setPreviousValidValue(value);
                      }}
                      onKeyDown={(e) =>
                        handleNumberKeyDown(
                          e,
                          value,
                          options.min?.value,
                          options.max?.value,
                          setValue,
                          name,
                          fractionDigits
                        )
                      }
                      className={classNames({
                        "p-invalid": errors[name],
                      })}
                      aria-invalid={errors[name]}
                      aria-autocomplete="none"
                    />
                    {options["required"] && <sup className="star-label">*</sup>}
                  </>
                );
              }
            }}
          />
        );
      case "dropdown":
        return (
          <Controller
            name={name}
            control={control}
            rules={options}
            defaultValue={options[0]?.value}
            render={({ field: { name, value, onChange } }) => {
              if (readOnly) {
                return (
                  <InputReadOnlyLabel
                    name={name}
                    value={
                      showDropdownOriginalValue
                        ? value
                        : getFieldLabel(value, dropdownOptions)
                    }
                  />
                );
              }
              let hideDropdown = null;
              return (
                <>
                  <Dropdown
                    id={name}
                    inputId={name}
                    value={setDropdownValue(value, dropdownOptions)}
                    onChange={(e) => {
                      const value = getDropdownValue(e.value);
                      onChange(value);
                    }}
                    className={classNames({
                      "p-invalid": errors[name],
                    })}
                    options={dropdownOptions}
                    optionLabel="label"
                    aria-invalid={errors[name]}
                    onShow={() => {
                      const openedDropdowns = openedDropdownsRef.current;
                      const openedDropdownsKeys =
                        Object.keys(openedDropdowns) || [];
                      if (openedDropdownsKeys.length) {
                        openedDropdownsKeys.forEach((dropdownKey) => {
                          openedDropdowns[dropdownKey]?.hideDropdown();
                        });
                      }
                      openedDropdownsRef.current[name] = { hideDropdown };
                    }}
                    onHide={() => {
                      delete openedDropdownsRef.current[name];
                    }}
                    ref={(ref) => {
                      if (ref) {
                        hideDropdown = ref.hide;
                      }
                    }}
                  />
                  {options["required"] && <sup className="star-label">*</sup>}
                </>
              );
            }}
          />
        );
      case "date": {
        const updatedOptions = getUpdatedOptions(options, readOnly, true);
        return (
          <>
            <Controller
              name={name}
              control={control}
              rules={updatedOptions}
              render={({ field: { name, value } }) => {
                if (readOnly) {
                  return <InputReadOnlyLabel name={name} value={value} />;
                }
                return (
                  <FormCalendarDate
                  {...{
                    name,
                    value,
                    errors,
                    clearErrors,
                    getDate,
                    setDate,
                    getChangedDateValues,
                    options,
                    dropdownStyle,
                    updatedOptions,
                    setError,
                  }}
                />
                );
              }}
            />
          </>
        );
      }
      case "lookup":
        const updatedOptions =
          inputType === "number"
            ? options
            : getUpdatedOptions(options, readOnly);
        {
          return (
            <>
              <Controller
                name={name}
                control={control}
                rules={updatedOptions}
                render={({ field: { name, value } }) => {
                  if (readOnly) {
                    if (viewMode === "text") {
                      return <span id={name}>{value}</span>;
                    }
                    const formData = getValues();
                    return (
                      <Button
                        label={value}
                        link
                        type="button"
                        onClick={() => {
                          viewLookupDetails(externalProgram, params, formData);
                        }}
                      />
                    );
                  }
                  return (
                    <>
                      {/* <IconField iconPosition="right">
                    <InputIcon className="pi pi-search"> </InputIcon>
                    <InputText
                      name={name}
                      value={value}
                      className={classNames({
                        "p-invalid": errors[name],
                      })}
                      aria-invalid={errors[name]}
                      onChange={(e) =>
                        handleTextChange(e, options.maxLength?.value)
                      }
                    />
                  </IconField> */}
                      <div className="p-inputgroup">
                        {inputType === "number" ? (
                          <InputNumber
                            id={name}
                            inputId={name}
                            name={name}
                            value={value}
                            min={options.min?.value}
                            max={options.max?.value}
                            mode={numberMode}
                            minFractionDigits={fractionDigits}
                            maxFractionDigits={fractionDigits}
                            onChange={(e) => {
                              const newValue = e.value;
                              handleNumberChange(
                                name,
                                newValue,
                                previousValidValue,
                                options.min?.value,
                                options.max?.value,
                                fractionDigits
                              );
                              setPreviousValidValue(value);
                            }}
                            onKeyDown={(e) => {
                              handleNumberKeyDown(
                                e,
                                value,
                                options?.min?.value,
                                options.max?.value,
                                setValue,
                                name,
                                fractionDigits
                              );
                              if (e.key === "F4") {
                                e.preventDefault();
                                openLookupModal();
                              }
                            }}
                            className={classNames({
                              "p-invalid": errors[name],
                            })}
                            aria-invalid={errors[name]}
                            aria-autocomplete="none"
                            tooltip="F4"
                          />
                        ) : (
                          <InputText
                            id={name}
                            name={name}
                            value={value}
                            className={classNames({
                              "p-invalid": errors[name],
                            })}
                            aria-invalid={errors[name]}
                            onChange={(e) =>
                              handleTextChange(e,options.maxLength?.value,true)
                            }
                            onKeyDown={(event) => {
                              const { key } = event;
                              if (key === "F4") {
                                event.preventDefault();
                                openLookupModal();
                              }
                            }}
                            autoComplete="off"
                            aria-autocomplete="none"
                            tooltip="F4"
                          />
                        )}
                        <Button
                          icon="pi pi-search"
                          className="lookup-button"
                          type="button"
                          onClick={() => {
                            openLookupModal();
                          }}
                        />
                      </div>
                      {options["required"] && (
                        <sup className="star-label-lookup">*</sup>
                      )}
                    </>
                  );
                }}
              />{" "}
            </>
          );
        }
      default:
        return null;
    }
  };

  if (!hidden) {
    return (
      <>
        {label && label.trim() !== "" && (
          <div
            className={`grid-item ${autoItemWidth ? "" : "label"} ${format}`}
          >
            <label htmlFor={name} className="content-input">
              {label}
            </label>
          </div>
        )}
        {type && type.toUpperCase().trim() !== "LABEL" && (
          <div className="grid-item input">
            {getComponent()}
            {errors[name] && (
              <small className="p-error">
                {processError(errors[name].message)}
              </small>
            )}
          </div>
        )}
      </>
    );
  }
  return null;
};

export default FormField;